import { useState } from "react";
import LazyLoad from "react-lazyload";

import dynamic from "next/dynamic";

import useResizeScreenModified from "common/hooks/useResizeScreenModified";

import ButtonNew from "components/atoms/ButtonNew/index";
import CheckContent from "components/atoms/CheckContent";
import Img from "components/atoms/Img";
import Title from "components/atoms/Title";
import CardRegistration from "components/molecules/CardRegistration";
import FormCountries from "components/molecules/FormCountries";
import FormNewsLetter from "components/molecules/FormNewsletter";
import RefundGuarantee from "components/molecules/RefundGuarantee";
import Banner from "components/organisms/Banner";
import EbookDownload from "components/organisms/EbookDownload/index";
import Faqs from "components/organisms/Faqs";
import OffshoreCompanyRegistration from "components/organisms/OffshoreCompanyRegistration";
import Packages from "components/organisms/PackagesNews";
import RequiredDocument from "components/organisms/RequiredDocument";
import WhatMakeUsDifferent from "components/organisms/WhatMakeUsDifferent";

import AnyIndustry from "./AnyIndustry";

const StepSlider = dynamic(() => import("components/organisms/StepSlider"), {
  ssr: false,
});

const CISlider = dynamic(() => import("./CISlider"), { ssr: false });
const NewsSlider = dynamic(() => import("components/organisms/NewsSlider"), {
  ssr: false,
});

const HkCompanyIncorporation = ({ data, dataJson }) => {
  const { country } = dataJson;

  const swiper = {
    direction: "vertical",
    preloadImages: false,
    lazy: true,
    autoHeight: true,
    autoplay: {
      delay: 2000,
      disableOnInteraction: false,
      pauseOnMouseEnter: true,
    },
    speed: 800,
    fadeEffect: { crossFade: true },
    virtualTranslate: true,
    effect: "fade",
    mousewheel: true,
    pagination: {
      el: ".bbc-slide-pagination",
      clickable: true,
      bulletClass: "bbc-pagination-bullet block my-1",
      bulletActiveClass: "bbc-pagination-bullet-active h-10 w-2.5",
    },
    slidesPerView: "auto",
    spaceBetween: 0,
  };
  const [screen, setScreen] = useState();

  useResizeScreenModified(setScreen);

  return (
    <>
      <Banner
        minHeightBanner="banner-min-h-635"
        breadcrumbs={dataJson.breadcrumbs}
        title={"Company formation <br/>in Hong Kong"}
        titleProps={{
          className: "banner-default-title mb-6 lg:mb-10 capitalize",
          textColor: "banner-title-default-color",
        }}
        description="<div class='text-bbc-brand-800'> <p class='mb-6'>Tired of incorporating your business the old way?</p><p class='mb-6'>We help global business owners, entrepreneurs and professionals like you start a company, hassle-free.</p><p>Our innovative solutions free you from the slow, repetitive and manual incorporation process. So you can focus on what really matters!</p></div>"
        leftClassName="lg:col-7"
        rightClassName="lg:col-5"
        rightContent={
          <Img
            wrapper={{
              className: "bbc-banner-image-country pb-10 lg:pb-0",
            }}
            src={`/assets/images/hk/${dataJson.banner_image}`}
            className="h-full w-full"
            width={560}
            height={456}
            priority={true}
            alt="tech-enabled Hong Kong incorporation service"
          />
        }
        bgColor={"bg-bbc-gradient-service"}
      ></Banner>
      <section className="mt-10 lg:mt-20">
        <OffshoreCompanyRegistration data={dataJson.dataRegistration} />
      </section>
      <section className="bg-bbc-gradient-9">
        <AnyIndustry data={dataJson.dataAnyIndustry} />
      </section>
      <section>
        {dataJson.dataOpenBankAccount &&
          typeof dataJson.dataOpenBankAccount === "object" &&
          Object.keys(dataJson.dataOpenBankAccount).length > 0 && (
            <LazyLoad>
              <StepSlider
                data={dataJson.dataOpenBankAccount}
                className="pt-0 lg:pt-0"
                title={dataJson.dataOpenBankAccount.title}
                imgAlt={dataJson.dataOpenBankAccount.alt}
              />
            </LazyLoad>
          )}
      </section>
      <Img
        src="/assets/images/wave/top-wave.png"
        width={1920}
        height={125}
        className="hidden w-full lg:block"
        wrapper={{ className: "w-full" }}
        alt="Wave top"
      />
      <div className="lg:bg-bbc-package">
        <div className="container ">
          <Packages
            data={data?.dataPackage || []}
            dataStatic={dataJson.dataPackage}
            title={`${
              country === "United Kingdom" ? "UK" : country
            } Company Formation Packages`}
          />

          <RefundGuarantee className="mb-10 pt-6 lg:mb-20 lg:pt-10" />
          <div className="row mb-10 items-center lg:mb-20">
            <div className="justify-self-start lg:col-6">
              <div className="h-full">
                <Title
                  text="Bring Your Next<br/> Business Venture Online"
                  className="bbc-title mb-6 text-center text-bbc-30px text-bbc-brand-800 lg:mb-0 lg:text-left lg:text-bbc-40px"
                />
              </div>
            </div>
            <div className="lg:col-6">
              <div className="h-full">
                <LazyLoad>
                  <iframe
                    src="https://www.youtube.com/embed/8RkJwKEXKPU?start=2"
                    width="540"
                    height="303"
                    frameBorder="0"
                    title="Bring your next business venture online"
                    allow="accelerometer; autoplay;encrypted-media; gyroscope;picture-in-picture"
                    allowFullScreen
                    className=" w-full"
                  ></iframe>
                </LazyLoad>
              </div>
            </div>
          </div>
        </div>
        <div className="mb-10 lg:mb-20">
          <WhatMakeUsDifferent
            title=""
            data={dataJson.dataWhatMakeUsDifferent}
            className="bg-wmud mb-10 py-[60px]"
          />

          <div className="container">
            {/* We make opening business bank account simple */}
            <div className="mb-10">
              <Title
                text="We make opening the business bank account simple"
                className="bbc-title mb-6 text-center"
              />
              <p>
                Opening an account with traditional banks is a good starting
                point if you want to grow your business. But if you’re a startup
                and your banking needs don’t include premium offerings from
                high-end providers. A Fintech-based account is a simpler option.
              </p>
              <div className="row relative mx-auto mt-6 w-full flex-col-reverse items-center p-0 lg:mx-auto lg:mt-10 lg:w-11/12 lg:flex-row">
                <LazyLoad>
                  <CISlider
                    dataJson={dataJson}
                    swiper={swiper}
                    screen={screen}
                  />
                </LazyLoad>
                <div className="col-12 right-0 top-0 bottom-0 my-auto flex items-center p-0 lg:col-6 lg:absolute">
                  <div className="w-full text-center ">
                    <Img
                      src={
                        "/assets/images/hk/company-incorporation/group_business-hk-incorporation.png"
                      }
                      width={478}
                      height={363}
                      alt="set up company in Hong Kong with a bank account"
                      className="w-full"
                      wrapper={{
                        className: "mb-6 w-full lg:mb-10 flex justify-center",
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
            {/* We simplify your company incorporation */}
            <div className="mb-10">
              <Title
                as="h2"
                className="mb-6 text-center text-3xl font-semibold  text-bbc-brand-800 lg:text-bbc-40px "
              >
                We simplify your company incorporation
              </Title>
              <p className="m-auto w-full  text-center lg:w-3/5">
                Our streamlined process is broken down into 4 simple steps,
                purposely designed to expand your confidence as you go.
              </p>
              <div className="row">
                {dataJson.dataHowItWorks?.length > 0 &&
                  dataJson.dataHowItWorks?.map((item, index) => (
                    <div
                      className={"col-12 mt-6 lg:col-4 lg:mt-10"}
                      key={index}
                    >
                      <CardRegistration index={index} item={item} />
                    </div>
                  ))}
              </div>
            </div>
            {/* Here's what your company looks like in terms of costs */}
            <div>
              <div className="row items-center">
                <div className="justify-self-start lg:col-6">
                  <div className="h-full">
                    <Title
                      text="Here's what your company looks like in terms of costs"
                      className="bbc-title text-bbc-30px text-bbc-brand-700"
                    />
                    <div className="hidden lg:inline-block">
                      <ButtonNew
                        as="a"
                        useRouter
                        href="/guide-and-tool/hong-kong-cost-planning-tool"
                        variant="outline"
                        othersClass={"mt-6 "}
                      >
                        Try It Now
                      </ButtonNew>
                    </div>
                  </div>
                </div>
                <div className="lg:col-6">
                  <div className="h-full text-center lg:text-right">
                    <Img
                      src="/assets/images/hk/company-incorporation/hk_tool-sg-incorporation.png"
                      width={448}
                      height={470}
                      wrapper={{
                        className: "flex justify-center lg:inline-block",
                      }}
                      alt="Hong Kong Tool"
                    />
                    <ButtonNew
                      as="a"
                      useRouter
                      href="/guide-and-tool/hong-kong-cost-planning-tool"
                      variant="outline"
                      othersClass={"mt-6 lg:hidden"}
                    >
                      Try It Now
                    </ButtonNew>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <RequiredDocument
          title="What documents to prepare?"
          data={dataJson.dataRequiredDocument}
          sectionClassName="py-10 lg:py-20 mb-10 "
          backgroundImage="/assets/images/common/required-documents.png"
        />
        <div className="container">
          <EbookDownload
            title={dataJson.dataEbookDownload.title}
            imgSrc={dataJson.dataEbookDownload.imgSrc}
            hash={dataJson.dataEbookDownload.hash}
            dataForm={dataJson.dataEbookDownload.dataForm}
            wrapper={{ className: "mb-10 lg:mb-20" }}
            alt={dataJson.dataEbookDownload.alt}
            form={dataJson.dataEbookDownload.form}
          >
            <p className="mb-6">
              Not sure about how to start a business? Crush your uncertainty and
              gain an extra edge with our Hong Kong Incorporation Checklist.
            </p>
            <ul>
              {[
                "Company launching requirements",
                "Steps and application procedure",
                "Checklist of business licenses",
                "Post-registration keynotes",
              ].map((item, index) => (
                <li key={index} className="mb-2 last:mb-0">
                  <CheckContent data={item} />
                </li>
              ))}
            </ul>
          </EbookDownload>
          <div>
            <Title
              text="Accounting solution for HK Start-ups and SMEs"
              className="bbc-title mb-10 text-center"
            />
            <div className="m-auto w-4/5">
              <div className="row">
                {dataJson.dataAccountingSolution.map((item, index) => (
                  <div key={index} className="col-12 text-center lg:col-3">
                    <Img
                      src={`/assets/icons/${item.icon}`}
                      alt={item.title}
                      width={48}
                      height={48}
                      className="h-12 w-12"
                      wrapper={{
                        className:
                          "bg-bbc-brand-100 rounded-3xl p-4 inline-block mb-3",
                      }}
                    />
                    <p className=" font-medium">{item.title}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className={"mt-10 text-center"}>
              <ButtonNew
                as="a"
                useRouter
                href="/accounting-and-auditing#caculator-price"
              >
                Get a quote
              </ButtonNew>
            </div>
          </div>
        </div>
      </div>
      <section className="relative">
        <div className=" absolute hidden h-1/2 w-full md:block lg:bg-bbc-package"></div>
        <div className="container relative  z-10 pt-10 lg:pt-20">
          <div className="m-auto lg:col-10">
            <FormCountries
              title={dataJson.dataFormContact.title}
              description={dataJson.dataFormContact.description}
              country_id={98}
            />
          </div>
        </div>
        <Img
          src="/assets/images/wave/bottom-wave.png"
          width={1920}
          height={140}
          className="hidden  w-full lg:block"
          wrapper={{ className: "absolute w-full top-1/2 hidden md:block" }}
          alt="Wave bottom"
        />
      </section>
      <section>
        <div className="container">
          <div className="lg:col-10 lg:m-auto">
            <Faqs
              data={dataJson.dataFaqs}
              title={dataJson.dataFaqs.title}
              description={dataJson.dataFaqs.description}
              defaultActiveKey={dataJson.dataFaqs.defaultActiveKey}
              multipleOpen={dataJson.dataFaqs.multipleOpen}
            />
          </div>
        </div>
      </section>
      <section className="">
        <div className="container">
          <Title className="title-h2 m-auto mb-10 w-2/3 pb-0 text-center text-bbc-brand-800">
            Industry News & Insights
          </Title>
          <LazyLoad>
            <NewsSlider data={data} />
          </LazyLoad>
        </div>
      </section>
      <section>
        <FormNewsLetter bgColor="bg-bbc-gradient-9" />
      </section>
    </>
  );
};

export default HkCompanyIncorporation;
